import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { adService } from "@/api/services"
import type { AdResponseDto, AdListParams, AdType } from "@/api/types/ad.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Loader2, X, Star, Zap, Sparkles } from "lucide-react"

export default function AdsListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [ads, setAds] = useState<AdResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)

  // Filter states
  const [search, setSearch] = useState("")
  const [adTypeFilter, setAdTypeFilter] = useState<AdType | undefined>(undefined)
  const [cityFilter, setCityFilter] = useState("")
  const [topOnly, setTopOnly] = useState(false)
  const [urgentOnly, setUrgentOnly] = useState(false)
  const [highlightOnly, setHighlightOnly] = useState(false)
  const [homepageHighlightOnly, setHomepageHighlightOnly] = useState(false)

  // Track if we've loaded initially
  const hasLoadedRef = useRef(false)

  // Debounce function
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    }, [value, delay])

    return debouncedValue
  }

  // Debounced search value
  const debouncedSearch = useDebounce(search, 500)

  // Track previous debounced search to avoid unnecessary updates
  const prevDebouncedSearch = useRef<string | undefined>(undefined)

  // Initial load on mount
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadAds()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload when navigating back to this page
  useEffect(() => {
    if (hasLoadedRef.current && location.pathname === "/ads") {
      const timer = setTimeout(() => {
        loadAds()
      }, 100)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Auto-apply filters when they change
  useEffect(() => {
    // Reset cursor when filters change (not when loading more)
    setCursor(null)
    setHasMore(false)
    
    // Only reload if search has actually changed
    const newSearch = debouncedSearch || undefined
    if (prevDebouncedSearch.current !== newSearch) {
      prevDebouncedSearch.current = newSearch
    }
    
    // Reload ads when any filter changes
    loadAds(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, adTypeFilter, cityFilter, topOnly, urgentOnly, highlightOnly, homepageHighlightOnly])

  const loadAds = async (loadMore = false) => {
    // Don't load more if we don't have a cursor (only applies when loading more, not initial load)
    if (loadMore && !cursor) {
      setHasMore(false)
      return
    }

    setLoading(true)
    setError("")
    try {
      const params: AdListParams = {
        limit: 30,
      }

      // Apply filters
      // Note: adType and special filters are NOT supported by API
      // All filtering is done client-side
      if (cityFilter && cityFilter.trim() !== "") {
        params.city = cityFilter.trim()
      }
      
      // Only add cursor for pagination (load more)
      if (loadMore && cursor) {
        params.cursor = cursor
      }

      const response = await adService.getAds(params)
      
      // Store raw response length BEFORE client-side filtering
      // This is important for pagination - we need to know if API returned a full page
      const rawResponseLength = response.data.length
      
      // Apply client-side filters
      let filteredData = response.data
      
      // Apply adType filter client-side (API doesn't support it)
      if (adTypeFilter) {
        filteredData = filteredData.filter((ad) => ad.adType === adTypeFilter)
      }
      
      // Apply special filters client-side (OR logic)
      // API doesn't support special filters, so we always filter client-side
      // Show ads that match ANY of the selected special filters
      const specialFiltersCount = [topOnly, urgentOnly, highlightOnly, homepageHighlightOnly].filter(Boolean).length
      
      if (specialFiltersCount > 0) {
        filteredData = filteredData.filter((ad) => {
          const matchesTop = topOnly && ad.isTop
          const matchesUrgent = urgentOnly && ad.isUrgent
          const matchesHighlight = highlightOnly && ad.isHighlight
          // HomepageHighlight: Check if ad has HomepageHighlight
          const matchesHomepageHighlight = homepageHighlightOnly && (ad.isHomepageHighlight || false)
          // Show ads that match ANY of the selected special filters (OR logic)
          return matchesTop || matchesUrgent || matchesHighlight || matchesHomepageHighlight
        })
      }
      // If no special filters are selected, show all ads
      
      // Apply client-side search filter if search term exists
      if (debouncedSearch && debouncedSearch.trim() !== "") {
        const searchLower = debouncedSearch.toLowerCase().trim()
        filteredData = filteredData.filter(
          (ad) =>
            ad.title.toLowerCase().includes(searchLower) ||
            ad.description.toLowerCase().includes(searchLower)
        )
      }
      
      if (loadMore) {
        // Append to existing ads when loading more
        setAds((prev) => [...prev, ...filteredData])
      } else {
        // Replace ads when filters change
        setAds(filteredData)
      }
      
      // Set cursor to the last ad's ID from the RAW response (before client-side filtering)
      // This ensures we paginate correctly even if client-side filtering reduces the count
      // hasMore is true if API returned a full page (30 ads) - meaning there might be more
      // We keep loading until API returns fewer than 30 ads (which means we've reached the end)
      if (rawResponseLength > 0) {
        // Use the last ad from raw response for cursor (not filtered data)
        const lastAdFromResponse = response.data[rawResponseLength - 1]
        setCursor(lastAdFromResponse.id)
        // If API returned exactly 30 ads, there are likely more to load
        // Only stop when API returns fewer than 30 ads (means we've reached the end)
        setHasMore(rawResponseLength >= 30)
      } else {
        // No ads returned from API - we've reached the end
        setCursor(null)
        setHasMore(false)
      }
    } catch (err: any) {
      console.error("[AdsListPage] Error loading ads:", err)
      const errorMessage = err.response?.data?.message || err.message || "Failed to load ads"
      setError(errorMessage)
      setAds([])
    } finally {
      setLoading(false)
    }
  }

  const handleAdClick = (ad: AdResponseDto) => {
    navigate(`/ads/${ad.id}`)
  }

  const handleClearFilters = () => {
    setSearch("")
    setAdTypeFilter(undefined)
    setCityFilter("")
    setTopOnly(false)
    setUrgentOnly(false)
    setHighlightOnly(false)
    setHomepageHighlightOnly(false)
    // Reset cursor when clearing filters
    setCursor(null)
    setHasMore(false)
  }

  const hasActiveFilters =
    search !== "" ||
    adTypeFilter !== undefined ||
    cityFilter !== "" ||
    topOnly ||
    urgentOnly ||
    highlightOnly ||
    homepageHighlightOnly

  const formatPrice = (price: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(parseFloat(price))
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Ads</h1>
              <p className="text-muted-foreground">View and manage all ads in the system</p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Search by title..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pr-8"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adType">Ad Type</Label>
                <select
                  id="adType"
                  value={adTypeFilter || ""}
                  onChange={(e) =>
                    setAdTypeFilter(e.target.value === "" ? undefined : (e.target.value as AdType))
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All Types</option>
                  <option value="Motorcycle">Motorcycle</option>
                  <option value="Gear">Gear</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="Filter by city"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Special Filters</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={topOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTopOnly(!topOnly)}
                  >
                    <Star className="mr-1 h-3 w-3" />
                    Top
                  </Button>
                  <Button
                    variant={urgentOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUrgentOnly(!urgentOnly)}
                  >
                    <Zap className="mr-1 h-3 w-3" />
                    Urgent
                  </Button>
                  <Button
                    variant={highlightOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHighlightOnly(!highlightOnly)}
                  >
                    <Sparkles className="mr-1 h-3 w-3" />
                    Highlight
                  </Button>
                  <Button
                    variant={homepageHighlightOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHomepageHighlightOnly(!homepageHighlightOnly)}
                    title="Homepage Highlight (API support coming soon)"
                  >
                    <Sparkles className="mr-1 h-3 w-3" />
                    Homepage Highlight
                  </Button>
                </div>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {ads.length} ad{ads.length !== 1 ? "s" : ""} found
                </p>
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              </div>
            )}
          </Card>

          {/* Ads Table */}
          <Card>
            {loading && ads.length === 0 ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !ads || ads.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-2">No ads found</p>
                {hasActiveFilters && (
                  <p className="text-xs">Try clearing your filters to see all ads</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto px-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Special</TableHead>
                      <TableHead>Published</TableHead>
                      <TableHead>Expires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ads.map((ad) => (
                      <TableRow
                        key={ad.id}
                        className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => handleAdClick(ad)}
                      >
                        <TableCell className="font-medium max-w-xs truncate">
                          {ad.title}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{ad.adType}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              ad.status === "Published"
                                ? "default"
                                : ad.status === "Draft"
                                ? "secondary"
                                : "outline"
                            }
                          >
                            {ad.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{formatPrice(ad.priceEur)}</TableCell>
                        <TableCell>{ad.locationCity || "N/A"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {ad.isTop && (
                              <Badge variant="default" className="text-xs">
                                <Star className="mr-1 h-3 w-3" />
                                Top
                              </Badge>
                            )}
                            {ad.isUrgent && (
                              <Badge variant="destructive" className="text-xs">
                                <Zap className="mr-1 h-3 w-3" />
                                Urgent
                              </Badge>
                            )}
                            {ad.isHighlight && (
                              <Badge variant="default" className="text-xs bg-yellow-500">
                                <Sparkles className="mr-1 h-3 w-3" />
                                Highlight
                              </Badge>
                            )}
                            {!ad.isTop && !ad.isUrgent && !ad.isHighlight && (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(ad.publishedAt)}</TableCell>
                        <TableCell>
                          {ad.expiresAt ? (
                            <span className={new Date(ad.expiresAt) < new Date() ? "text-destructive" : ""}>
                              {formatDate(ad.expiresAt)}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Load More Button */}
                {hasMore && (
                  <div className="p-6 text-center">
                    <Button
                      variant="outline"
                      onClick={() => loadAds(true)}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load More"
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  )
}

